/**
 * DiscountAuthPanel — Fév 2026
 *
 * Panneau Admin permettant :
 *   • d'ouvrir une "fenêtre d'autorisation" pour que le Resp. Op. applique
 *     des réductions (durée en minutes OU fenêtre horaire absolue)
 *   • de fixer un plafond (max %, max F CFA)
 *   • de révoquer l'autorisation en cours
 *   • de voir le journal des réductions appliquées
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ShieldCheck, ShieldOff, Clock, Percent, Coins, Loader2, RefreshCw,
  KeyRound, History, TrendingDown, Pencil, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 🆕 (Fév 2026) — Groupes de restrictions métier prédéfinis.
// Chaque groupe mappe soit un `department` Caisse, soit un matcher de category_id
// (keywords case+accent-insensitive). Le frontend résout ces groupes en
// `allowed_departments` + `allowed_category_ids` avant l'envoi backend.
const RESTRICTION_GROUPS = [
  { key: "bar",             label: "Bar",                emoji: "🍺", match: { departments: ["bar"] },
    hint: "Tous les produits du département Bar (bières, sodas, cocktails, jus, eau…)" },
  { key: "fastfood",        label: "Fast Food",          emoji: "🍔",
    match: { keywords: ["burger", "sandwich", "chawarma", "pizza", "frite", "fastfood"] },
    hint: "Burgers, Chawarma/Sandwichs, Pizzas, Frites" },
  { key: "restauration",    label: "Restauration",       emoji: "🍽",
    match: { keywords: ["grillade", "plat", "pate", "riz", "feculent", "traditionnel"] },
    hint: "Grillades, Plats, Pâtes, Riz, Féculents, Traditionnel" },
  { key: "entrees_salades", label: "Entrées & Salades",  emoji: "🥗",
    match: { keywords: ["entree", "salade"] },
    hint: "Entrées + Salades" },
  { key: "desserts",        label: "Desserts",           emoji: "🍰",
    match: { keywords: ["dessert", "jus"] },
    hint: "Desserts + Jus" },
  { key: "jeux",            label: "Jeux & Simulateur",  emoji: "🎮",
    match: { departments: ["jeux"], keywords: ["jeux vr", "simulateur", "vr"] },
    hint: "Jeux VR, Simulateur" },
];

const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const resolveGroups = (groupKeys, availableCategories) => {
  const departments = new Set();
  const category_ids = new Set();
  for (const key of groupKeys) {
    const g = RESTRICTION_GROUPS.find(x => x.key === key);
    if (!g) continue;
    for (const d of (g.match.departments || [])) departments.add(d);
    const kws = (g.match.keywords || []).map(norm);
    for (const c of availableCategories) {
      const cname = norm(c.name);
      if (kws.some(k => cname.includes(k))) category_ids.add(c.id);
    }
  }
  return { departments: [...departments], category_ids: [...category_ids] };
};

// 🆕 Reconstruit les groupes cochés à partir d'une autorisation active (mode Édition).
// Un groupe est considéré comme sélectionné si TOUTES ses catégories/départements
// résolus sont présents dans l'autorisation active.
const detectSelectedGroups = (auth, availableCategories) => {
  if (!auth) return [];
  const activeDeps = new Set(auth.allowed_departments || []);
  const activeCats = new Set(auth.allowed_category_ids || []);
  const detected = [];
  for (const g of RESTRICTION_GROUPS) {
    const r = resolveGroups([g.key], availableCategories);
    const size = r.departments.length + r.category_ids.length;
    if (size === 0) continue;
    const depOk = r.departments.every(d => activeDeps.has(d));
    const catOk = r.category_ids.every(c => activeCats.has(c));
    if (depOk && catOk) detected.push(g.key);
  }
  return detected;
};

const fmtRemaining = (s) => {
  if (!s || s <= 0) return "—";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${m} min`;
};

export default function DiscountAuthPanel({ currentUser }) {
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(Date.now());
  const [editing, setEditing] = useState(false); // 🆕 mode édition d'une auth active
  const [extendMinutes, setExtendMinutes] = useState(0); // 🆕 minutes à ajouter
  // Form state
  const [mode, setMode] = useState("duration"); // duration | window
  const [durationMin, setDurationMin] = useState(120);
  const [validFrom, setValidFrom] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [maxPct, setMaxPct] = useState(30);
  const [maxAmt, setMaxAmt] = useState(10000);
  const [notes, setNotes] = useState("");
  // 🆕 Restriction produits
  const [restrictProducts, setRestrictProducts] = useState(false);
  const [allowedProductIds, setAllowedProductIds] = useState([]);
  const [allowedCategoryIds, setAllowedCategoryIds] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]); // 🆕 clés RESTRICTION_GROUPS
  const [productSearch, setProductSearch] = useState("");
  const [availableProducts, setAvailableProducts] = useState([]);
  // Log
  const [log, setLog] = useState([]);
  const [logTotal, setLogTotal] = useState(0);

  const fetchActive = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/discounts/authorizations/active`);
      setActive(r.data.active ? { ...r.data.authorization, remaining_seconds: r.data.remaining_seconds } : null);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  const fetchLog = useCallback(async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const r = await axios.get(`${API}/discounts/log`, { params: { limit: 20, date_from: today } });
      setLog(r.data.log || []);
      setLogTotal(r.data.total_saved || 0);
    } catch { /* silent */ }
  }, []);

  // 🆕 Fetch catalogue produits (pour restriction éventuelle)
  const fetchProducts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/caisse/products`);
      const list = Array.isArray(r.data) ? r.data : (r.data.products || []);
      setAvailableProducts(list);
      // Déduit les catégories depuis les produits (name unique par category_id)
      const catMap = {};
      for (const p of list) {
        const cid = p.category_id || p.category;
        if (cid && !catMap[cid]) {
          catMap[cid] = { id: cid, name: p.category_name || p.category_label || cid };
        }
      }
      setAvailableCategories(Object.values(catMap).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchActive();
    fetchLog();
    fetchProducts();
  }, [fetchActive, fetchLog, fetchProducts]);

  // Tick clock for remaining display
  useEffect(() => {
    const t = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const remainingSeconds = active
    ? Math.max(0, Math.floor((new Date(active.valid_until).getTime() - tick) / 1000))
    : 0;

  const grant = async () => {
    setBusy(true);
    try {
      // 🆕 Résout les groupes sélectionnés → departments + category_ids
      const resolved = restrictProducts
        ? resolveGroups(selectedGroups, availableCategories)
        : { departments: [], category_ids: [] };
      const body = {
        granted_by: currentUser?.full_name || currentUser?.username || "Admin",
        granted_by_role: "admin",
        max_percent: parseFloat(maxPct) || null,
        max_amount: parseFloat(maxAmt) || null,
        notes,
        allowed_product_ids: restrictProducts ? allowedProductIds : [],
        allowed_category_ids: resolved.category_ids,
        allowed_departments: resolved.departments,
      };
      if (mode === "duration") {
        body.duration_minutes = parseInt(durationMin) || 60;
      } else {
        if (!validFrom || !validUntil) {
          toast.error("Renseignez les 2 dates");
          setBusy(false);
          return;
        }
        body.valid_from = new Date(validFrom).toISOString();
        body.valid_until = new Date(validUntil).toISOString();
      }
      await axios.post(`${API}/discounts/authorizations`, body);
      toast.success("Autorisation ouverte");
      setNotes("");
      setRestrictProducts(false);
      setAllowedProductIds([]);
      setAllowedCategoryIds([]);
      setSelectedGroups([]);
      setProductSearch("");
      fetchActive();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!active) return;
    if (!window.confirm("Révoquer l'autorisation en cours ?")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/discounts/authorizations/${active.id}/revoke`, {
        revoked_by: currentUser?.full_name || "Admin",
        reason: "Révocation manuelle",
      });
      toast.success("Autorisation révoquée");
      fetchActive();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  // 🆕 Passe en mode Édition : pré-remplit le formulaire avec les valeurs actives
  const startEdit = () => {
    if (!active) return;
    setEditing(true);
    setMaxPct(active.max_percent ?? 30);
    setMaxAmt(active.max_amount ?? 10000);
    setNotes(active.notes || "");
    const hasRestriction = (active.allowed_product_ids?.length || 0) > 0
      || (active.allowed_category_ids?.length || 0) > 0
      || (active.allowed_departments?.length || 0) > 0;
    setRestrictProducts(hasRestriction);
    setAllowedProductIds(active.allowed_product_ids || []);
    setAllowedCategoryIds(active.allowed_category_ids || []);
    setSelectedGroups(detectSelectedGroups(active, availableCategories));
    setExtendMinutes(0);
  };

  const cancelEdit = () => {
    setEditing(false);
    setExtendMinutes(0);
  };

  // 🆕 Met à jour l'autorisation active (élargir/rétrécir la liste, ajuster les plafonds, prolonger)
  const updateAuth = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const resolved = restrictProducts
        ? resolveGroups(selectedGroups, availableCategories)
        : { departments: [], category_ids: [] };
      const body = {
        updated_by: currentUser?.full_name || currentUser?.username || "Admin",
        updated_by_role: "admin",
        max_percent: parseFloat(maxPct) || null,
        max_amount: parseFloat(maxAmt) || null,
        notes,
        allowed_product_ids: restrictProducts ? allowedProductIds : [],
        allowed_category_ids: resolved.category_ids,
        allowed_departments: resolved.departments,
      };
      const em = parseInt(extendMinutes);
      if (em > 0) body.extend_minutes = em;
      await axios.patch(`${API}/discounts/authorizations/${active.id}`, body);
      toast.success(em > 0 ? `Autorisation mise à jour (+${em} min)` : "Autorisation mise à jour");
      setEditing(false);
      setExtendMinutes(0);
      fetchActive();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="discount-auth-panel">
      {/* Statut actuel */}
      <Card className={`border-2 ${active
        ? "bg-gradient-to-br from-emerald-950/50 via-emerald-900/20 to-slate-900/80 border-emerald-500/50"
        : "bg-slate-900/70 border-slate-700"}`}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3 flex-wrap">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
              active ? "bg-emerald-500/25 border border-emerald-500/50" : "bg-slate-800 border border-slate-700"
            }`}>
              {active ? <ShieldCheck className="w-6 h-6 text-emerald-300" /> : <ShieldOff className="w-6 h-6 text-slate-500" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Autorisation Remises</p>
              <h3 className={`font-black text-lg leading-tight ${active ? "text-emerald-200" : "text-slate-300"}`}>
                {active ? "OUVERTE" : "FERMÉE"}
              </h3>
              {active && (
                <div className="mt-1 space-y-0.5 text-[11px]">
                  <p className="text-slate-300">
                    Expire dans <span className="font-mono font-black text-emerald-300 text-sm">{fmtRemaining(remainingSeconds)}</span>
                    <span className="text-slate-500 ml-2">
                      (jusqu&apos;au {new Date(active.valid_until).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })})
                    </span>
                  </p>
                  <div className="flex items-center gap-2 flex-wrap mt-1">
                    {active.max_percent !== null && (
                      <Badge className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/40 text-[10px]">
                        <Percent className="w-2.5 h-2.5 mr-0.5" /> Max {active.max_percent}%
                      </Badge>
                    )}
                    {active.max_amount !== null && (
                      <Badge className="bg-amber-500/20 text-amber-200 border border-amber-500/40 text-[10px]">
                        <Coins className="w-2.5 h-2.5 mr-0.5" /> Max {Number(active.max_amount).toLocaleString("fr-FR")} F
                      </Badge>
                    )}
                    {active.allowed_product_ids && active.allowed_product_ids.length > 0 && (
                      <Badge className="bg-violet-500/20 text-violet-200 border border-violet-500/40 text-[10px] font-bold" title={active.allowed_product_ids.map(id => availableProducts.find(p => p.id === id)?.name).filter(Boolean).join(", ")}>
                        🎯 {active.allowed_product_ids.length} article{active.allowed_product_ids.length > 1 ? "s" : ""} éligible{active.allowed_product_ids.length > 1 ? "s" : ""}
                      </Badge>
                    )}
                    {active.allowed_category_ids && active.allowed_category_ids.length > 0 && (
                      <Badge className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/40 text-[10px] font-bold" title={active.allowed_category_ids.map(id => availableCategories.find(c => c.id === id)?.name).filter(Boolean).join(", ")}>
                        📁 {active.allowed_category_ids.length} catégorie{active.allowed_category_ids.length > 1 ? "s" : ""}
                      </Badge>
                    )}
                    <span className="text-slate-500 text-[10px]">par <b className="text-slate-400">{active.granted_by}</b></span>
                  </div>
                  {active.notes && <p className="text-slate-400 italic mt-1">« {active.notes} »</p>}
                </div>
              )}
              {!active && (
                <p className="text-slate-500 text-xs mt-1">
                  Ouvrez une fenêtre ci-dessous pour autoriser le Resp. Op. à appliquer des réductions.
                </p>
              )}
            </div>
            {active && !editing && (
              <>
                <Button
                  onClick={startEdit}
                  disabled={busy}
                  variant="outline"
                  className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
                  data-testid="discount-auth-edit"
                >
                  <Pencil className="w-4 h-4 mr-1" /> Modifier
                </Button>
                <Button
                  onClick={revoke}
                  disabled={busy}
                  variant="outline"
                  className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                  data-testid="discount-auth-revoke"
                >
                  <ShieldOff className="w-4 h-4 mr-1" /> Révoquer
                </Button>
              </>
            )}
            {active && editing && (
              <Button
                onClick={cancelEdit}
                disabled={busy}
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
                data-testid="discount-auth-cancel-edit"
              >
                <X className="w-4 h-4 mr-1" /> Annuler édition
              </Button>
            )}
            <Button
              onClick={() => { fetchActive(); fetchLog(); }}
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white"
              data-testid="discount-auth-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Formulaire d'octroi / d'édition */}
      {(!active || editing) && (
        <Card className={`${editing ? "bg-cyan-950/40 border-cyan-500/50" : "bg-slate-900/60 border-slate-700"}`}>
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              {editing ? (
                <><Pencil className="w-4 h-4 text-cyan-400" /> Modifier l&apos;autorisation en cours</>
              ) : (
                <><KeyRound className="w-4 h-4 text-amber-400" /> Ouvrir une fenêtre d&apos;autorisation</>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {editing ? (
              /* 🆕 Mode Édition — pas de choix de fenêtre, mais possibilité de prolonger */
              <div className="p-3 rounded-xl border-2 border-cyan-500/40 bg-cyan-500/5">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-cyan-300" />
                  <span className="font-bold text-sm text-cyan-100">Prolonger la fenêtre (optionnel)</span>
                </div>
                <p className="text-[11px] text-slate-400 mb-2">
                  Expire actuellement dans <span className="font-mono text-cyan-200 font-bold">{fmtRemaining(remainingSeconds)}</span>.
                  Ajoutez du temps si nécessaire :
                </p>
                <div className="flex gap-2 flex-wrap items-center">
                  {[0, 15, 30, 60, 120].map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setExtendMinutes(m)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold ${
                        Number(extendMinutes) === m
                          ? "bg-cyan-500/30 text-cyan-100 border border-cyan-500/60"
                          : "bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-600"
                      }`}
                      data-testid={`discount-auth-extend-${m}`}
                    >
                      {m === 0 ? "Aucune" : (m < 60 ? `+${m}m` : `+${Math.floor(m/60)}h${m%60 ? ` ${m%60}m` : ""}`)}
                    </button>
                  ))}
                  <Input
                    type="number"
                    value={extendMinutes}
                    onChange={e => setExtendMinutes(e.target.value)}
                    className="bg-slate-900 border-slate-700 text-white h-8 w-24 text-center font-mono"
                    min={0}
                    data-testid="discount-auth-extend-input"
                  />
                  <span className="text-[11px] text-slate-500">min</span>
                </div>
              </div>
            ) : (
              <>
                {/* Mode choice (création uniquement) */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setMode("duration")}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      mode === "duration"
                        ? "bg-emerald-500/15 border-emerald-500/60 text-white"
                        : "bg-slate-900/50 border-slate-700 text-slate-400 hover:border-slate-600"
                    }`}
                    data-testid="discount-auth-mode-duration"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4" />
                      <span className="font-bold text-sm">Durée</span>
                    </div>
                    <p className="text-[11px] text-slate-500">Dès maintenant pour X minutes</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("window")}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      mode === "window"
                        ? "bg-emerald-500/15 border-emerald-500/60 text-white"
                        : "bg-slate-900/50 border-slate-700 text-slate-400 hover:border-slate-600"
                    }`}
                    data-testid="discount-auth-mode-window"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4" />
                      <span className="font-bold text-sm">Fenêtre horaire</span>
                    </div>
                    <p className="text-[11px] text-slate-500">Date/heure début → fin</p>
                  </button>
                </div>

                {mode === "duration" ? (
                  <div>
                    <Label className="text-slate-400 text-xs uppercase tracking-wider">Durée (minutes)</Label>
                    <div className="flex gap-2 mt-1 flex-wrap">
                      {[30, 60, 120, 180, 240, 480].map(m => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => setDurationMin(m)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold ${
                            durationMin === m ? "bg-emerald-500/30 text-emerald-100 border border-emerald-500/50" : "bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-600"
                          }`}
                        >
                          {m < 60 ? `${m}m` : `${Math.floor(m/60)}h${m%60 ? ` ${m%60}m` : ""}`}
                        </button>
                      ))}
                      <Input
                        type="number"
                        value={durationMin}
                        onChange={e => setDurationMin(e.target.value)}
                        className="bg-slate-900 border-slate-700 text-white h-8 w-24 text-center font-mono"
                        min={1}
                        data-testid="discount-auth-duration-input"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs uppercase tracking-wider">Début</Label>
                      <Input
                        type="datetime-local"
                        value={validFrom}
                        onChange={e => setValidFrom(e.target.value)}
                        className="bg-slate-900 border-slate-700 text-white h-9 mt-1"
                        data-testid="discount-auth-from"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs uppercase tracking-wider">Fin</Label>
                      <Input
                        type="datetime-local"
                        value={validUntil}
                        onChange={e => setValidUntil(e.target.value)}
                        className="bg-slate-900 border-slate-700 text-white h-9 mt-1"
                        data-testid="discount-auth-until"
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Plafonds */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-wider">Taux max (%)</Label>
                <Input
                  type="number"
                  value={maxPct}
                  onChange={e => setMaxPct(e.target.value)}
                  min={0} max={100}
                  className="bg-slate-900 border-slate-700 text-white h-9 mt-1 font-mono"
                  data-testid="discount-auth-max-percent"
                />
              </div>
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-wider">Forfait max (F)</Label>
                <Input
                  type="number"
                  value={maxAmt}
                  onChange={e => setMaxAmt(e.target.value)}
                  min={0}
                  className="bg-slate-900 border-slate-700 text-white h-9 mt-1 font-mono"
                  data-testid="discount-auth-max-amount"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-wider">Notes (optionnel)</Label>
              <Input
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Ex : Happy hour vendredi soir"
                className="bg-slate-900 border-slate-700 text-white h-9 mt-1"
                data-testid="discount-auth-notes"
              />
            </div>

            {/* 🆕 Restriction produits */}
            <div className="border border-slate-700 rounded-xl p-3 bg-slate-950/50">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={restrictProducts}
                  onChange={e => { setRestrictProducts(e.target.checked); if (!e.target.checked) setAllowedProductIds([]); }}
                  className="w-4 h-4 accent-emerald-500 cursor-pointer"
                  data-testid="discount-auth-restrict-toggle"
                />
                <span className="text-sm font-bold text-white">Restreindre à certains articles uniquement</span>
              </label>
              <p className="text-[11px] text-slate-500 mt-1 ml-6">
                Si activé, seuls les produits sélectionnés pourront recevoir une remise du Resp. Op. (les remises sur facture entière sont désactivées).
              </p>

              {restrictProducts && (
                <div className="mt-3 space-y-3" data-testid="discount-auth-products-selector">
                  {/* 🆕 (Fév 2026) — 6 groupes de restrictions métier prédéfinis */}
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1.5">
                    Groupes autorisés (cochez ceux à autoriser)
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {RESTRICTION_GROUPS.map(g => {
                      const selected = selectedGroups.includes(g.key);
                      return (
                        <button
                          key={g.key}
                          type="button"
                          onClick={() => setSelectedGroups(a => selected ? a.filter(x => x !== g.key) : [...a, g.key])}
                          className={`p-2.5 rounded-xl border-2 text-left transition-all ${
                            selected
                              ? "bg-emerald-500/15 border-emerald-500/60 text-white shadow-lg shadow-emerald-500/10"
                              : "bg-slate-900/50 border-slate-700 text-slate-300 hover:border-slate-500"
                          }`}
                          data-testid={`discount-auth-group-${g.key}`}
                          title={g.hint}
                        >
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className="text-lg leading-none">{g.emoji}</span>
                            <span className="font-bold text-sm">{selected ? "✓ " : ""}{g.label}</span>
                          </div>
                          <p className="text-[10px] text-slate-400 leading-tight">{g.hint}</p>
                        </button>
                      );
                    })}
                  </div>

                  {/* Résumé résolu — transparence : ce que ces groupes couvriront */}
                  {selectedGroups.length > 0 && (() => {
                    const resolved = resolveGroups(selectedGroups, availableCategories);
                    return (
                      <div className="p-2.5 bg-emerald-500/5 border border-emerald-500/30 rounded-lg text-[11px] text-slate-300">
                        <p className="text-emerald-300 uppercase tracking-widest text-[10px] font-bold mb-1">
                          ✅ Éligibilité résolue
                        </p>
                        {resolved.departments.length > 0 && (
                          <p>
                            <span className="text-slate-400">Départements :</span>{" "}
                            <span className="font-mono text-emerald-200">{resolved.departments.join(", ")}</span>
                          </p>
                        )}
                        {resolved.category_ids.length > 0 && (
                          <p>
                            <span className="text-slate-400">Catégories matchées :</span>{" "}
                            <span className="text-emerald-200">
                              {resolved.category_ids
                                .map(id => availableCategories.find(c => c.id === id)?.name)
                                .filter(Boolean)
                                .join(", ") || "—"}
                            </span>
                          </p>
                        )}
                        {resolved.departments.length === 0 && resolved.category_ids.length === 0 && (
                          <p className="text-amber-400 italic">
                            Aucune catégorie du catalogue ne matche ces groupes — vérifiez que vos produits ont les bons libellés.
                          </p>
                        )}
                      </div>
                    );
                  })()}

                  {restrictProducts && selectedGroups.length === 0 && (
                    <p className="text-[11px] text-amber-400 italic">
                      ⚠ Aucun groupe sélectionné — le Resp. Op. ne pourra rien remiser.
                    </p>
                  )}
                </div>
              )}
            </div>

            <Button
              onClick={editing ? updateAuth : grant}
              disabled={busy}
              className={`w-full font-bold text-white ${editing ? "bg-cyan-600 hover:bg-cyan-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
              data-testid={editing ? "discount-auth-update" : "discount-auth-grant"}
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (editing ? <Pencil className="w-4 h-4 mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />)}
              {editing ? "Enregistrer les modifications" : "Ouvrir l'autorisation"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Journal des réductions du jour */}
      <Card className="bg-slate-900/60 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <History className="w-4 h-4 text-cyan-400" /> Réductions du jour
            </span>
            <Badge className="bg-rose-500/20 text-rose-200 border border-rose-500/40 font-mono">
              <TrendingDown className="w-3 h-3 mr-1" />
              -{Number(logTotal).toLocaleString("fr-FR")} F
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {log.length === 0 ? (
            <p className="p-6 text-center text-slate-500 text-sm">Aucune réduction appliquée aujourd&apos;hui.</p>
          ) : (
            <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden mx-3 mb-3">
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-950/70 border-b border-slate-800 sticky top-0 z-10">
                    <tr className="text-left text-slate-400">
                      <th className="py-2.5 px-3 font-semibold uppercase text-[10px] tracking-widest">Heure</th>
                      <th className="py-2.5 px-3 font-semibold uppercase text-[10px] tracking-widest">Qui</th>
                      <th className="py-2.5 px-3 font-semibold uppercase text-[10px] tracking-widest">Sur</th>
                      <th className="py-2.5 px-3 font-semibold uppercase text-[10px] tracking-widest text-right">Remise</th>
                      <th className="py-2.5 px-3 font-semibold uppercase text-[10px] tracking-widest text-right">Économie</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/80">
                    {log.map((l, idx) => (
                      <tr
                        key={l.id}
                        className={`border-l-2 border-l-rose-500 hover:bg-rose-500/5 ${idx % 2 === 1 ? "bg-slate-900/40" : "bg-slate-900/20"}`}
                        data-testid={`discount-log-row-${l.id}`}
                      >
                        <td className="py-2 px-3 text-slate-400 font-mono text-[11px] whitespace-nowrap">
                          {new Date(l.applied_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                        </td>
                        <td className="py-2 px-3 text-white whitespace-nowrap">
                          <span className="font-medium">{l.applied_by}</span>
                          <Badge className={`ml-1.5 text-[9px] ${l.applied_by_role === "admin" ? "bg-violet-500/20 text-violet-200 border border-violet-500/40" : "bg-cyan-500/20 text-cyan-200 border border-cyan-500/40"}`}>
                            {l.applied_by_role}
                          </Badge>
                        </td>
                        <td className="py-2 px-3 text-slate-300 truncate max-w-[200px]">
                          {l.scope === "order" ? (
                            <Badge className="bg-amber-500/15 text-amber-200 border border-amber-500/40 text-[10px]">Facture entière</Badge>
                          ) : (
                            <span title={l.product_name}>{l.product_name || "—"}</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right font-mono font-bold text-rose-300 whitespace-nowrap">
                          {l.discount_type === "percent" ? `-${l.discount_value}%` : `-${Number(l.discount_value).toLocaleString("fr-FR")} F`}
                        </td>
                        <td className="py-2 px-3 text-right font-mono text-slate-400 whitespace-nowrap">
                          -{Number(l.amount_saved).toLocaleString("fr-FR")} F
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
